import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import axios from "axios";
import { toast } from "sonner";
import { 
  CheckCircle2, 
  Clock, 
  Shield, 
  PlayCircle,
  ArrowRight,
  AlertTriangle,
  Loader2,
  CreditCard,
  Scale,
  BookOpen,
  GraduationCap,
  HelpCircle,
  Video,
  Star,
  ShoppingCart
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FreeTrainingPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [registered, setRegistered] = useState(false);

  // Check if user has already registered (stored in localStorage)
  useEffect(() => {
    const hasRegistered = localStorage.getItem('freeTrainingRegistered');
    if (hasRegistered) {
      setRegistered(true);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email) {
      toast.error("Please enter your name and email");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/training-registration`, {
        ...formData,
        training: "free-training-debt-options",
        source: "free-training-page"
      });
      
      localStorage.setItem('freeTrainingRegistered', 'true');
      setRegistered(true);
      toast.success("Welcome! Your free training is ready to watch.");
    } catch (error) {
      console.error("Error registering:", error);
      toast.error("Unable to register. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const warningSignsLeft = [
    "Only paying minimum payments (and struggling)",
    "Using credit cards to pay other credit cards",
    "Collectors calling daily",
    "Hiding spending from family"
  ];

  const warningSignsRight = [
    "Losing sleep over bills",
    "Paycheck gone before it arrives",
    "Avoiding mail and phone calls",
    "Feeling hopeless about money"
  ];

  const whatYoullLearn = [
    "The 3 real options when debt becomes overwhelming",
    "How to calculate if your debt is \"excessive\" (simple formula)",
    "Debt Consolidation: When it works and when it doesn't",
    "Credit Counseling: What to expect and red flags to avoid",
    "Bankruptcy: The truth about fresh starts (from someone who filed 3 times)",
    "Which option is right for YOUR situation"
  ];

  const options = [
    {
      icon: CreditCard,
      title: "Debt Consolidation",
      description: "Combine multiple debts into one payment, often at lower interest",
      best: "Best for: Those with decent credit who need simplification"
    },
    {
      icon: HelpCircle,
      title: "Credit Counseling",
      description: "Work with professionals who negotiate with creditors on your behalf",
      best: "Best for: Those who need guidance and accountability"
    },
    {
      icon: Scale,
      title: "Bankruptcy",
      description: "Legal elimination of qualifying debts - a true fresh start",
      best: "Best for: Those who need complete debt relief"
    }
  ];

  const ebooks = [
    {
      id: "ebook-1",
      title: "Stop Living Paycheck to Paycheck",
      price: "$9.99",
      originalPrice: "$19.99",
      onSale: true,
      image: "https://customer-assets.emergentagent.com/job_omw-academy/artifacts/ljfzsvtw_IMG_5405.png"
    },
    {
      id: "ebook-5",
      title: "Bankruptcy to Breakthrough",
      price: "$14.99",
      image: "https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=800"
    },
    {
      id: "ebook-2",
      title: "Building Business Credit",
      price: "$24.99",
      image: "https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=800"
    }
  ];

  const courses = [
    {
      id: "course-1",
      title: "Business Startup Basics",
      description: "Everything you need to launch your business the right way",
      price: "$199",
      modules: 8
    },
    {
      id: "course-2",
      title: "Business Credit Mastery",
      description: "Build fundable business credit from scratch",
      price: "$249",
      modules: 6
    }
  ];

  // ============================================
  // REGISTERED VIEW - Video + CTAs
  // ============================================
  if (registered) {
    return (
      <div className="min-h-screen bg-white" data-testid="free-training-video-page">
        {/* Video Hero Section */}
        <section className="bg-gradient-to-br from-[#1E3A5F] via-[#162c47] to-[#0f1e32] py-12 lg:py-16">
          <div className="max-w-5xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 bg-green-500 text-white px-4 py-2 rounded-full text-sm font-bold mb-4">
                <CheckCircle2 className="h-4 w-4" />
                YOU'RE IN! WATCH NOW
              </div>
              <h1 className="text-3xl lg:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                How to Know When It's Time to Act on Excessive Debt
              </h1>
              <p className="text-gray-300 text-lg">
                Watch this 32-minute training to discover your best path forward
              </p>
            </div>

            {/* Vimeo Video Embed */}
            <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-black aspect-video">
              <iframe
                src="https://player.vimeo.com/video/1168719255?badge=0&autopause=0&player_id=0&app_id=58479"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
                allowFullScreen
                className="absolute top-0 left-0 w-full h-full"
                title="Free Training - Time to Act on Debt"
                data-testid="training-video"
              />
            </div>

            <div className="flex items-center justify-center gap-6 mt-6 text-gray-300">
              <span className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                32:25 minutes
              </span>
              <span className="flex items-center gap-2">
                <Video className="h-4 w-4" />
                HD Quality
              </span>
            </div>
          </div>
        </section>

        {/* What You Just Learned */}
        <section className="py-12 bg-gray-50">
          <div className="max-w-4xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-8">
              <h2 className="text-2xl lg:text-3xl font-bold text-[#1E3A5F]" style={{ fontFamily: 'Playfair Display, serif' }}>
                Key Takeaways From This Training
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {options.map((option, index) => (
                <div key={index} className="bg-white rounded-xl p-6 text-center shadow-sm">
                  <div className="bg-[#1E3A5F] w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4">
                    <option.icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-[#1E3A5F] mb-2">{option.title}</h3>
                  <p className="text-gray-600 text-sm">{option.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FREE BONUS: Financial Clarity Workbook */}
        <section className="py-12 bg-white">
          <div className="max-w-4xl mx-auto px-6 lg:px-8">
            <div className="bg-gradient-to-r from-[#1E3A5F] to-[#2d4a6f] rounded-2xl p-8 lg:p-10 text-white">
              <div className="flex flex-col lg:flex-row items-center gap-8">
                <div className="flex-shrink-0">
                  <div className="bg-[#9D9045] w-24 h-24 rounded-2xl flex items-center justify-center">
                    <BookOpen className="h-12 w-12 text-white" />
                  </div>
                </div>
                <div className="flex-1 text-center lg:text-left">
                  <div className="inline-flex items-center gap-2 bg-green-500 px-3 py-1 rounded-full text-sm font-bold mb-3">
                    <CheckCircle2 className="h-4 w-4" />
                    FREE BONUS INCLUDED
                  </div>
                  <h3 className="text-2xl lg:text-3xl font-bold mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                    The Financial Clarity Workbook
                  </h3>
                  <p className="text-gray-300 mb-4">
                    Put what you learned into action! This interactive workbook helps you audit your spending, 
                    calculate your debt ratio, and decide which path is right for YOU.
                  </p>
                  <ul className="text-sm text-gray-300 mb-6 space-y-2">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-[#9D9045]" />
                      Subscription & spending audit exercises
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-[#9D9045]" />
                      Automatic debt ratio calculator
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-[#9D9045]" />
                      Decision framework for your situation
                    </li>
                  </ul>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button 
                      onClick={() => navigate('/workbook')}
                      size="lg"
                      className="bg-[#9D9045] hover:bg-[#8a7e3c] text-white font-bold"
                    >
                      Start Interactive Workbook
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                    <Button 
                      onClick={() => window.open('/financial-clarity-workbook.md', '_blank')}
                      size="lg"
                      variant="outline"
                      className="border-white/30 text-white hover:bg-white/10"
                    >
                      Download PDF Version
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Ready to Take Action - Ebooks */}
        <section className="py-16 bg-white">
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-10">
              <span className="text-[#9D9045] font-semibold text-sm tracking-wider uppercase">Ready to Take Action?</span>
              <h2 className="text-3xl lg:text-4xl font-bold text-[#1E3A5F] mt-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                Continue Your Journey With Our Resources
              </h2>
              <p className="text-gray-600 mt-3 text-lg">Practical guides to help you take the next step</p>
            </div>

            {/* Ebook Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-10">
              {ebooks.map((ebook) => (
                <div key={ebook.id} className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100 hover:shadow-xl transition-shadow group">
                  <div className="relative">
                    <img 
                      src={ebook.image} 
                      alt={ebook.title}
                      className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    {ebook.onSale && (
                      <div className="absolute top-3 right-3 bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                        SALE
                      </div>
                    )}
                  </div>
                  <div className="p-5">
                    <h3 className="font-bold text-[#1E3A5F] text-lg mb-2">{ebook.title}</h3>
                    <div className="flex items-center gap-2 mb-4">
                      <span className="text-2xl font-bold text-[#9D9045]">{ebook.price}</span>
                      {ebook.originalPrice && (
                        <span className="text-gray-400 line-through text-sm">{ebook.originalPrice}</span>
                      )}
                    </div>
                    <Button 
                      onClick={() => navigate('/ebooks')}
                      className="w-full bg-[#1E3A5F] hover:bg-[#2d4a6f] text-white"
                    >
                      <BookOpen className="mr-2 h-4 w-4" />
                      View Details
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {/* Bundle CTA */}
            <div className="bg-gradient-to-r from-[#9D9045] to-[#b8a94f] rounded-2xl p-8 text-center text-white">
              <div className="inline-flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full text-sm font-bold mb-4">
                <Star className="h-4 w-4" />
                BEST VALUE
              </div>
              <h3 className="text-2xl font-bold mb-2">Get All 6 Ebooks - Save 25%</h3>
              <p className="text-white/90 mb-6">Complete financial transformation bundle</p>
              <div className="flex items-center justify-center gap-4 mb-6">
                <span className="text-3xl font-bold">$82.46</span>
                <span className="text-white/60 line-through">$109.94</span>
              </div>
              <Button 
                onClick={() => navigate('/ebooks')}
                size="lg"
                className="bg-white text-[#9D9045] hover:bg-gray-100 font-bold px-8"
              >
                <ShoppingCart className="mr-2 h-5 w-5" />
                Get the Bundle
              </Button>
            </div>
          </div>
        </section>

        {/* Business School Courses */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-5xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-10">
              <span className="text-[#9D9045] font-semibold text-sm tracking-wider uppercase">Go Deeper</span>
              <h2 className="text-3xl lg:text-4xl font-bold text-[#1E3A5F] mt-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                Business Bootcamp Institute Courses
              </h2>
              <p className="text-gray-600 mt-3 text-lg">Comprehensive training to build your business and credit</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {courses.map((course) => (
                <div key={course.id} className="bg-white rounded-xl shadow-lg p-8 border border-gray-100 hover:shadow-xl transition-shadow">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="bg-[#1E3A5F] w-14 h-14 rounded-xl flex items-center justify-center">
                      <GraduationCap className="h-7 w-7 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#1E3A5F] text-xl">{course.title}</h3>
                      <p className="text-gray-500 text-sm">{course.modules} Modules</p>
                    </div>
                  </div>
                  <p className="text-gray-600 mb-6">{course.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-bold text-[#9D9045]">{course.price}</span>
                    <Button 
                      onClick={() => navigate('/business-school')}
                      className="bg-[#9D9045] hover:bg-[#8a7e3c] text-white"
                    >
                      Learn More
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mt-10">
              <Button 
                onClick={() => navigate('/business-school')}
                variant="outline"
                size="lg"
                className="border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#1E3A5F] hover:text-white"
              >
                View All Courses
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        </section>

        {/* Need Personal Help? */}
        <section className="py-16 bg-[#1E3A5F]">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Need Personalized Guidance?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Book a one-on-one consultation to discuss your specific situation
            </p>
            <Button 
              onClick={() => navigate('/services')}
              size="lg"
              className="bg-[#9D9045] hover:bg-[#8a7e3c] text-white px-10 py-6 text-lg font-bold"
            >
              View Consulting Services
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-[#0f1e32] py-8 text-center">
          <p className="text-gray-400 text-sm">
            © 2024 OMW Business & Financial Resources. All Rights Reserved.
          </p>
          <p className="text-gray-500 text-xs mt-2">
            Veteran-Owned Small Business | omwbusiness.com
          </p>
        </footer>
      </div>
    );
  }

  // ============================================
  // REGISTRATION VIEW - Sign Up Form
  // ============================================
  return (
    <div className="min-h-screen bg-white" data-testid="free-training-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#1E3A5F] via-[#162c47] to-[#0f1e32] py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left - Text */}
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-full text-sm font-bold mb-6">
                <AlertTriangle className="h-4 w-4" />
                DROWNING IN DEBT?
              </div>
              
              <h1 className="text-3xl lg:text-4xl xl:text-5xl font-bold text-white mb-6 leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
                How to Know When It's Time to Act on Excessive Debt
              </h1>
              
              <p className="text-lg text-gray-300 mb-6">
                If you're overwhelmed by debt, you're not alone—and you're not out of options.
              </p>
              
              <p className="text-xl text-[#9D9045] font-semibold mb-8">
                Learn your 3 real options: Debt Consolidation, Credit Counseling, or Bankruptcy
              </p>

              <div className="flex items-center gap-3 mb-4">
                <PlayCircle className="h-6 w-6 text-[#9D9045]" />
                <span className="text-white font-medium">FREE 32-Minute Training</span>
              </div>
              
              <div className="flex items-center gap-3 mb-8">
                <Video className="h-6 w-6 text-[#9D9045]" />
                <span className="text-white font-medium">Instant Access - Watch Now</span>
              </div>

              {/* Mobile CTA */}
              <div className="lg:hidden mb-8">
                <a href="#register">
                  <Button size="lg" className="w-full bg-[#9D9045] hover:bg-[#8a7e3c] text-white py-6 text-lg font-bold">
                    Get Free Access Now
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </a>
              </div>
            </div>

            {/* Right - Registration Form */}
            <div className="hidden lg:block" id="register">
              <div className="bg-white rounded-2xl p-8 shadow-2xl">
                <div className="text-center mb-6">
                  <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-semibold mb-3">
                    <PlayCircle className="h-4 w-4" />
                    INSTANT ACCESS
                  </div>
                  <h2 className="text-2xl font-bold text-[#1E3A5F]" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Watch the Free Training
                  </h2>
                  <p className="text-gray-600 mt-2">Enter your info to unlock the video</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <Input
                    type="text"
                    placeholder="Your Full Name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="py-6"
                    data-testid="training-name"
                  />
                  <Input
                    type="email"
                    placeholder="Your Best Email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="py-6"
                    data-testid="training-email"
                  />
                  <Input
                    type="tel"
                    placeholder="Phone Number (Optional)"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="py-6"
                    data-testid="training-phone"
                  />
                  <Button 
                    type="submit"
                    size="lg"
                    className="w-full bg-[#9D9045] hover:bg-[#8a7e3c] text-white py-6 text-lg font-bold"
                    disabled={submitting}
                    data-testid="training-submit"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Getting Access...
                      </>
                    ) : (
                      <>
                        <PlayCircle className="mr-2 h-5 w-5" />
                        Watch Free Training Now
                      </>
                    )}
                  </Button>
                </form>

                <p className="text-gray-500 text-xs text-center mt-4 flex items-center justify-center gap-2">
                  <Shield className="h-4 w-4" />
                  100% Free. No credit card required.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Warning Signs */}
      <section className="py-16 bg-red-50">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-10">
            <AlertTriangle className="h-12 w-12 text-red-600 mx-auto mb-4" />
            <h2 className="text-2xl lg:text-3xl font-bold text-[#1E3A5F]" style={{ fontFamily: 'Playfair Display, serif' }}>
              Warning Signs Your Debt Has Become Excessive
            </h2>
            <p className="text-gray-600 mt-2">If 3 or more apply to you, it's time to act.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              {warningSignsLeft.map((sign, index) => (
                <div key={index} className="flex items-center gap-3 bg-white p-4 rounded-lg shadow-sm">
                  <div className="bg-red-100 p-2 rounded-full">
                    <AlertTriangle className="h-4 w-4 text-red-600" />
                  </div>
                  <span className="text-gray-700">{sign}</span>
                </div>
              ))}
            </div>
            <div className="space-y-3">
              {warningSignsRight.map((sign, index) => (
                <div key={index} className="flex items-center gap-3 bg-white p-4 rounded-lg shadow-sm">
                  <div className="bg-red-100 p-2 rounded-full">
                    <AlertTriangle className="h-4 w-4 text-red-600" />
                  </div>
                  <span className="text-gray-700">{sign}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="text-center mt-8">
            <p className="text-lg text-[#1E3A5F] font-semibold">Sound familiar? You have options.</p>
          </div>
        </div>
      </section>

      {/* Your 3 Options */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-[#9D9045] font-semibold text-sm tracking-wider uppercase">In This Free Training</span>
            <h2 className="text-3xl lg:text-4xl font-bold text-[#1E3A5F] mt-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              Your 3 Real Options
            </h2>
            <p className="text-gray-600 mt-3 text-lg">I'll help you understand which one is right for you.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {options.map((option, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-6 text-center hover:shadow-lg transition-shadow">
                <div className="bg-[#1E3A5F] w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <option.icon className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-[#1E3A5F] mb-3">{option.title}</h3>
                <p className="text-gray-600 mb-4">{option.description}</p>
                <p className="text-sm text-[#9D9045] font-medium">{option.best}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What You'll Learn */}
      <section className="py-16 lg:py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-[#1E3A5F]" style={{ fontFamily: 'Playfair Display, serif' }}>
              What You'll Learn
            </h2>
          </div>

          <div className="space-y-4">
            {whatYoullLearn.map((item, index) => (
              <div 
                key={index}
                className="flex items-start gap-4 bg-white p-5 rounded-xl shadow-sm"
              >
                <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700 text-lg">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Listen to Me */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="bg-[#1E3A5F] rounded-2xl p-8 lg:p-12 text-white">
            <h2 className="text-2xl lg:text-3xl font-bold mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              Why Should You Listen to Me?
            </h2>
            
            <div className="space-y-4 text-gray-200">
              <p>
                Because I've been exactly where you are. Not once. Not twice. <strong className="text-white">Three times.</strong>
              </p>
              <p>
                I filed my first bankruptcy—a Chapter 13—but the attorney wasn't honest with me. I fired him and filed Chapter 7 a month later. 
                Then, years later, life happened again. Third bankruptcy.
              </p>
              <p>
                I lost cars. I lost homes. I know what it feels like to dread the phone ringing and avoid the mailbox.
              </p>
              <p>
                But here's what I also know: <strong className="text-[#9D9045]">I recovered.</strong>
              </p>
              <p>
                I rebuilt my credit to <strong className="text-white">over 800</strong>. I purchased a home with <strong className="text-white">$0 down</strong>. 
                I even bought a Mercedes <em>during</em> my last bankruptcy proceeding.
              </p>
              <p>
                With <strong className="text-white">over a decade in banking</strong> and <strong className="text-white">25+ years</strong> in 
                financial services, I know both sides—the industry AND the struggle.
              </p>
              <p className="text-xl font-semibold text-white pt-4">
                I'll help you understand your options so you can make the right decision for YOUR situation.
              </p>
            </div>

            <div className="mt-8 text-right">
              <p className="text-[#9D9045] font-semibold">— Lavista Smith</p>
              <p className="text-gray-400 text-sm">Founder, OMW Business & Financial Resources</p>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile Registration Form */}
      <section className="lg:hidden py-16 bg-[#1E3A5F]" id="register">
        <div className="max-w-md mx-auto px-6">
          <div className="bg-white rounded-2xl p-8 shadow-2xl">
            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-semibold mb-3">
                <PlayCircle className="h-4 w-4" />
                INSTANT ACCESS
              </div>
              <h2 className="text-2xl font-bold text-[#1E3A5F]" style={{ fontFamily: 'Playfair Display, serif' }}>
                Watch the Free Training
              </h2>
              <p className="text-gray-600 mt-2">Enter your info to unlock the video</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                type="text"
                placeholder="Your Full Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="py-6"
              />
              <Input
                type="email"
                placeholder="Your Best Email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="py-6"
              />
              <Input
                type="tel"
                placeholder="Phone Number (Optional)"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="py-6"
              />
              <Button 
                type="submit"
                size="lg"
                className="w-full bg-[#9D9045] hover:bg-[#8a7e3c] text-white py-6 text-lg font-bold"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Getting Access...
                  </>
                ) : (
                  <>
                    <PlayCircle className="mr-2 h-5 w-5" />
                    Watch Free Training Now
                  </>
                )}
              </Button>
            </form>

            <p className="text-gray-500 text-xs text-center mt-4 flex items-center justify-center gap-2">
              <Shield className="h-4 w-4" />
              100% Free. No credit card required.
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="hidden lg:block py-16 bg-gradient-to-br from-[#1E3A5F] via-[#162c47] to-[#0f1e32]">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <Clock className="h-12 w-12 text-[#9D9045] mx-auto mb-6" />
          <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Stop Wondering. Start Knowing.
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            The worst thing you can do is nothing. Get clarity on your options today.
          </p>
          <a href="#register">
            <Button size="lg" className="bg-[#9D9045] hover:bg-[#8a7e3c] text-white px-12 py-6 text-xl font-bold">
              <PlayCircle className="mr-2 h-5 w-5" />
              Watch Free Training Now
            </Button>
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0f1e32] py-8 text-center">
        <p className="text-gray-400 text-sm">
          © 2024 OMW Business & Financial Resources. All Rights Reserved.
        </p>
        <p className="text-gray-500 text-xs mt-2">
          Veteran-Owned Small Business | omwbusiness.com
        </p>
      </footer>
    </div>
  );
};

export default FreeTrainingPage;
